/**
 * Bounded sessionStorage snapshot for returning-client intake resume after /signin.
 * PHI-minimal: pre-verification demographics + preferences only (no screening answers).
 */

import type { Dispatch } from 'react';
import type { IntakeAction, IntakeAnswers } from './types';
import { needsLocationStep } from './types';

const STORAGE_KEY = 'unify-intake-resume-v1';
const TTL_MS = 30 * 60 * 1000;

export interface IntakeResumeSnapshot {
  savedAt: number;
  patientId: string;
  answers: Pick<
    IntakeAnswers,
    | 'therapyTypes'
    | 'therapyType'
    | 'topics'
    | 'specialties'
    | 'demographicPrefs'
    | 'language'
    | 'format'
    | 'availabilityTimeOfDay'
    | 'availabilityDays'
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'dateOfBirth'
    | 'zip'
    | 'payType'
    | 'preferredLocationIds'
  >;
}

export function saveIntakeResumeSnapshot(snapshot: Omit<IntakeResumeSnapshot, 'savedAt'>): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...snapshot, savedAt: Date.now() } satisfies IntakeResumeSnapshot),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function clearIntakeResumeSnapshot(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function tryRestoreIntakeResumeSnapshot(
  dispatch: Dispatch<IntakeAction>,
  opts: { resumePatientId?: string },
): boolean {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as IntakeResumeSnapshot;
    if (!parsed?.patientId || !parsed.savedAt) {
      clearIntakeResumeSnapshot();
      return false;
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      clearIntakeResumeSnapshot();
      return false;
    }
    if (opts.resumePatientId && parsed.patientId !== opts.resumePatientId) {
      return false;
    }

    dispatch({
      type: 'RESTORE_INTAKE_RESUME',
      payload: {
        answers: parsed.answers,
        patientId: parsed.patientId,
        step: needsLocationStep(parsed.answers.format ?? null) ? 7 : 8,
      },
    });
    clearIntakeResumeSnapshot();
    return true;
  } catch {
    clearIntakeResumeSnapshot();
    return false;
  }
}

export function buildIntakeResumeSnapshot(
  answers: IntakeAnswers,
  patientId: string,
): Omit<IntakeResumeSnapshot, 'savedAt'> {
  return {
    patientId,
    answers: {
      therapyTypes: answers.therapyTypes,
      therapyType: answers.therapyType,
      topics: answers.topics,
      specialties: answers.specialties,
      demographicPrefs: answers.demographicPrefs,
      language: answers.language,
      format: answers.format,
      availabilityTimeOfDay: answers.availabilityTimeOfDay,
      availabilityDays: answers.availabilityDays,
      firstName: answers.firstName,
      lastName: answers.lastName,
      email: answers.email,
      dateOfBirth: answers.dateOfBirth,
      zip: answers.zip,
      payType: answers.payType,
      preferredLocationIds: answers.preferredLocationIds,
    },
  };
}

export const INTAKE_SIGNIN_RETURN_PATH = '/signin?returnTo=%2Fintake';
